import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { buyShares, depositToBank, getMarketplace, getMyInvestments, getMyWallets, withdrawFromBank } from "@/lib/economy-api";
import { formatGros } from "@/lib/currency";
import { firstParagraph } from "@/lib/text";
import { ASSETS } from "@/assets";
import { AccountShell } from "@/components/account/AccountShell";
import { CoinAmount } from "@/components/economy/CoinAmount";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INVESTMENTS, MARKETPLACE, WALLET_TRANSFER } from "@/constants/testIds";

const WalletCard = ({ wallet, onChanged }) => {
  const [direction, setDirection] = useState("deposit"); // "deposit" (cash -> bank) | "withdraw" (bank -> cash)
  const [amount, setAmount] = useState(0);
  const [busy, setBusy] = useState(false);

  const maxAmount = direction === "deposit" ? wallet.money_gros : Math.floor(Number(wallet.balance_gros));

  const handleTransfer = async () => {
    if (amount <= 0) return;
    setBusy(true);
    try {
      const fn = direction === "deposit" ? depositToBank : withdrawFromBank;
      await fn(wallet.character_guid, amount);
      toast(direction === "deposit" ? "Vloženo do banky" : "Vybráno z banky", {
        description: `${wallet.character_name}: převedeno ${amount} grošů.`,
      });
      setAmount(0);
      onChanged();
    } catch (err) {
      toast("Převod se nezdařil", { description: err.response?.data?.detail ?? "Zkus to znovu." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={INVESTMENTS.walletChip}
      className="rounded-xl border border-[#F0801F]/40 bg-[#17171b] p-4"
    >
      <p className="flex items-center gap-1.5 font-display text-[10px] font-600 uppercase tracking-[0.2em] text-[#F0801F]">
        <i className="fa-solid fa-landmark" />
        Konto v Horowitzově bance
      </p>
      <p className="mt-1.5 text-xs text-white/50">{wallet.character_name}</p>
      <CoinAmount amount={wallet.balance_gros} size="lg" className="mt-2.5" />

      <p className="mb-1.5 mt-4 text-[11px] uppercase tracking-[0.14em] text-white/40">Hotovost u sebe</p>
      <CoinAmount amount={wallet.money_gros} size="md" />

      {wallet.is_online ? (
        <p className="mt-4 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2.5 text-xs text-white/50" data-testid={WALLET_TRANSFER.onlineNotice}>
          Postava je právě ve hře - pro převod peněz se musí nejdřív odhlásit.
        </p>
      ) : (
        <div className="mt-4 space-y-2.5 border-t border-white/10 pt-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setDirection("deposit");
                setAmount(0);
              }}
              data-testid={WALLET_TRANSFER.directionDeposit}
              className={`flex-1 rounded-md border py-1.5 text-[11px] uppercase tracking-wide ${
                direction === "deposit" ? "border-[#F0801F] text-[#F0801F]" : "border-white/20 text-white/60"
              }`}
            >
              Vložit do banky
            </button>
            <button
              type="button"
              onClick={() => {
                setDirection("withdraw");
                setAmount(0);
              }}
              data-testid={WALLET_TRANSFER.directionWithdraw}
              className={`flex-1 rounded-md border py-1.5 text-[11px] uppercase tracking-wide ${
                direction === "withdraw" ? "border-[#F0801F] text-[#F0801F]" : "border-white/20 text-white/60"
              }`}
            >
              Vybrat z banky
            </button>
          </div>

          {maxAmount <= 0 ? (
            <p className="text-xs text-white/40">Není co převést.</p>
          ) : (
            <>
              <input
                type="range"
                min={0}
                max={maxAmount}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                data-testid={WALLET_TRANSFER.slider}
                className="w-full accent-[#F0801F]"
              />
              <div className="flex items-center justify-between">
                <CoinAmount amount={amount} size="md" />
                <button
                  type="button"
                  disabled={busy || amount <= 0}
                  onClick={handleTransfer}
                  data-testid={WALLET_TRANSFER.submitButton}
                  className="rounded-md bg-[#F0801F] px-4 py-1.5 text-xs font-600 uppercase tracking-wide text-[#141417] hover:bg-[#ff9438] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? "…" : "Převést"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const MarketplaceCard = ({ company, characters, onBought }) => {
  const [characterGuid, setCharacterGuid] = useState(characters[0]?.character_guid ?? "");
  const [shares, setShares] = useState(1);
  const [busy, setBusy] = useState(false);

  const available = company.available_for_sale;
  const price = (Number(company.price_per_share_gros) * shares).toString();

  const handleBuy = async () => {
    if (!characterGuid) {
      toast("Vyber postavu", { description: "Nejdřív vyber, která postava má podíl koupit." });
      return;
    }
    setBusy(true);
    try {
      await buyShares(company.id, { character_guid: Number(characterGuid), shares });
      toast("Podíl koupen", { description: `Koupil(a) jsi ${shares} % podniku „${company.name}“.` });
      setShares(1);
      onBought();
    } catch (err) {
      toast("Nákup se nezdařil", { description: err.response?.data?.detail ?? "Zkus to znovu." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={MARKETPLACE.companyCard}
      className="rounded-xl border border-white/10 bg-[#17171b] p-6"
    >
      <Link to={`/ekonomika/${company.id}`} data-testid={MARKETPLACE.companyLink} className="block">
        {company.image_url && (
          <img
            src={company.image_url}
            alt={company.name}
            className="mb-4 h-32 w-full rounded-lg object-cover"
          />
        )}
        <div className="flex items-center justify-between">
          <span className="font-display text-base font-600 text-white hover:text-[#F0801F]">{company.name}</span>
          <span className="rounded-full border border-[#F0801F]/40 px-3 py-1 text-xs font-600 text-[#F0801F]">
            {available} % volných
          </span>
        </div>
      </Link>
      {company.description && (
        <p className="mt-2 text-sm text-white/50">{firstParagraph(company.description)}</p>
      )}
      <p className="mt-2 text-xs text-white/40">
        Vlastník: {company.npc_owner_name} · Cena za 1 % podílu: {formatGros(company.price_per_share_gros)} ·
        Týdenní zisk: {formatGros(company.weekly_profit_gros)}
      </p>

      {available === 0 ? (
        <p className="mt-4 text-sm text-white/40">Momentálně není nic k prodeji.</p>
      ) : characters.length === 0 ? (
        <p className="mt-4 text-sm text-white/40">Nemáš žádnou postavu, která by mohla nakupovat.</p>
      ) : (
        <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
          <Select value={String(characterGuid)} onValueChange={setCharacterGuid}>
            <SelectTrigger
              data-testid={MARKETPLACE.characterSelect}
              className="w-full rounded-md border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none focus:border-[#F0801F]/60 focus:ring-0"
            >
              <SelectValue placeholder="Vyber postavu" />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#1e1e23] text-white">
              {characters.map((c) => (
                <SelectItem
                  key={c.character_guid}
                  value={String(c.character_guid)}
                  className="text-white focus:bg-[#F0801F]/15 focus:text-[#F0801F]"
                >
                  {c.character_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div>
            <div className="mb-1.5 flex items-center justify-between text-xs text-white/50">
              <span>Kolik procent koupit</span>
              <span className="text-[#F0801F]">{shares} %</span>
            </div>
            <input
              type="range"
              min={1}
              max={available}
              value={shares}
              onChange={(e) => setShares(Number(e.target.value))}
              data-testid={MARKETPLACE.sharesSlider}
              className="w-full accent-[#F0801F]"
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-white/50">Cena celkem</span>
            <span className="text-[#F0801F]">{formatGros(price)}</span>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={handleBuy}
            data-testid={MARKETPLACE.buyButton}
            className="w-full rounded-md bg-[#F0801F] py-2.5 font-display text-xs font-600 uppercase tracking-[0.12em] text-[#141417] hover:bg-[#ff9438] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? "Kupuji…" : "Koupit podíl"}
          </button>
        </div>
      )}
    </div>
  );
};

export const InvestmentsPage = () => {
  const { user, loading } = useAuth();
  const [investments, setInvestments] = useState(null);
  const [wallets, setWallets] = useState(null);
  const [marketplace, setMarketplace] = useState(null);

  const refresh = useCallback(() => {
    Promise.all([getMyInvestments(), getMyWallets(), getMarketplace()]).then(([inv, w, m]) => {
      setInvestments(inv);
      setWallets(w);
      setMarketplace(m);
    });
  }, []);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  if (loading) {
    return (
      <section className="min-h-[70vh] px-6 pb-24 pt-40 text-center">
        <p className="font-display text-sm uppercase tracking-[0.2em] text-white/40">Načítám…</p>
      </section>
    );
  }

  if (!user) return <Navigate to="/ucet" replace />;

  const hasAnyHolding = investments?.characters?.some((c) => c.holdings.length > 0);
  const characterOptions = wallets ?? [];

  return (
    <AccountShell isGm={user.is_gm} testId={INVESTMENTS.page}>
      <div className="flex items-center gap-5">
        <img
          src={ASSETS.logoBanka}
          alt="Horowitzova městská banka"
          className="h-20 w-20 shrink-0 rounded-full border border-white/10 bg-[#17171b] object-contain p-2 sm:h-24 sm:w-24"
        />
        <div>
          <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
            Horowitzova městská banka
          </p>
          <h1 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
            Moje investice
          </h1>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-white/10 bg-[#17171b] p-6 sm:p-8">
        <div className="mb-3 flex items-center gap-2 text-[#F0801F]">
          <i className="fa-solid fa-scroll text-sm" />
          <p className="font-display text-xs font-600 uppercase tracking-[0.2em]">O bance</p>
        </div>
        <p className="text-sm leading-relaxed text-white/60">
          Horowitzova městská banka otevřela svou první pobočku v Rowengardu před pěti lety,
          krátce po udělení oficiálního statusu královského pohraničního města Cormyrského
          království. Od svého příchodu se stala spolehlivým místem pro obyvatele, obchodníky
          i cestovatele, kteří hledají bezpečné uložení svých prostředků a důvěryhodné finanční
          služby.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-white/60">
          Dnes má Horowitzova městská banka více než osmnáct poboček po celém Cormyru. Ačkoli
          patří mezi menší bankovní domy království, její síť se neustále rozšiřuje a díky
          pevným zásadám, osobnímu přístupu a rostoucí důvěře zákazníků si postupně buduje své
          místo mezi významnými finančními institucemi země.
        </p>
      </div>

      {wallets && wallets.length > 0 && (
        <Accordion
          type="single"
          collapsible
          defaultValue="wallets"
          className="mt-6 rounded-xl border border-white/10 bg-[#17171b] px-6"
        >
          <AccordionItem value="wallets" className="border-none">
            <AccordionTrigger className="font-display text-sm font-600 uppercase tracking-[0.12em] text-white hover:text-[#F0801F] hover:no-underline">
              Postavy a jejich konta ({wallets.length})
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 gap-3 pb-2 sm:grid-cols-2 lg:grid-cols-3">
                {wallets.map((w) => (
                  <WalletCard key={w.character_guid} wallet={w} onChanged={refresh} />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {investments === null && <p className="mt-10 text-sm text-white/40">Načítám investice…</p>}

      {investments && !hasAnyHolding && (
        <div
          className="mt-10 rounded-xl border border-white/10 bg-[#17171b] p-8 text-center"
          data-testid={INVESTMENTS.empty}
        >
          <p className="text-sm text-white/50">
            Zatím nevlastníš žádný podíl. Podívej se níže, jaké podniky ve městě nabízejí podíl k prodeji.
          </p>
        </div>
      )}

      {investments && hasAnyHolding && (
        <Accordion
          type="single"
          collapsible
          defaultValue="holdings"
          className="mt-10 rounded-xl border border-white/10 bg-[#17171b] px-6"
        >
          <AccordionItem value="holdings" className="border-none">
            <AccordionTrigger className="font-display text-sm font-600 uppercase tracking-[0.12em] text-white hover:text-[#F0801F] hover:no-underline">
              Moje podíly v podnicích
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-8 pb-2">
                {investments.characters
                  .filter((c) => c.holdings.length > 0)
                  .map((c) => (
                    <div key={c.character_guid} data-testid={INVESTMENTS.characterSection}>
                      <h2 className="mb-4 font-display text-lg font-600 uppercase tracking-tight text-white">
                        {c.character_name}
                      </h2>
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {c.holdings.map((h) => (
                          <Link
                            key={h.company_id}
                            to={`/ekonomika/${h.company_id}`}
                            data-testid={INVESTMENTS.holdingCard}
                            className="block rounded-xl border border-white/10 bg-[#17171b] p-6 transition-colors hover:border-[#F0801F]/40"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-display text-base font-600 text-white">{h.company_name}</span>
                              <span className="rounded-full border border-[#F0801F]/40 px-3 py-1 text-xs font-600 text-[#F0801F]">
                                {h.percent} %
                              </span>
                            </div>
                            {h.is_majority_owner && (
                              <span className="mt-2 inline-block rounded-full bg-[#F0801F]/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#F0801F]">
                                Většinový vlastník
                              </span>
                            )}
                            <dl className="mt-4 space-y-3 text-sm text-white/60">
                              <div>
                                <dt className="mb-1 text-xs">Hodnota podílu</dt>
                                <dd><CoinAmount amount={h.stake_value_gros} size="sm" /></dd>
                              </div>
                              <div>
                                <dt className="mb-1 text-xs">Týdenní výnos</dt>
                                <dd><CoinAmount amount={h.weekly_yield_gros} size="sm" /></dd>
                              </div>
                              <div>
                                <dt className="mb-1 text-xs">Nevyplacený zbytek</dt>
                                <dd><CoinAmount amount={h.unpaid_remainder_gros} size="sm" showFraction /></dd>
                              </div>
                              <div>
                                <dt className="mb-1 text-xs">Celkem vyděláno</dt>
                                <dd><CoinAmount amount={h.total_paid_gros} size="sm" /></dd>
                              </div>
                            </dl>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      <div className="mt-14" data-testid={MARKETPLACE.section}>
        <h2 className="mb-4 font-display text-lg font-600 uppercase tracking-tight text-white">
          Podniky k investování
        </h2>
        {marketplace === null && <p className="text-sm text-white/40">Načítám nabídku…</p>}
        {marketplace && marketplace.length === 0 && (
          <p className="text-sm text-white/50" data-testid={MARKETPLACE.empty}>
            Ve městě zatím není žádný podnik.
          </p>
        )}
        {marketplace && marketplace.length > 0 && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {marketplace.map((company) => (
              <MarketplaceCard
                key={company.id}
                company={company}
                characters={characterOptions}
                onBought={refresh}
              />
            ))}
          </div>
        )}
      </div>
    </AccountShell>
  );
};
